import React from "react";
import { cn } from "@/lib/utils";

/**
 * Select — native `<select>` styled to match the design language.
 *
 * We intentionally use the platform-native control (not a Radix
 * combobox) at the primitive layer for accessibility + zero-dep
 * reliability. Composite dropdowns can be built on top later.
 *
 * Props:
 *   • options: [{ value, label, disabled? }] — required
 *   • placeholder — renders an empty disabled option
 *   • invalid, size — same as Input
 */

const SIZES = {
  sm: "h-8  pl-2 pr-8 text-[13px]",
  md: "h-10 pl-3 pr-9 text-[14px]",
  lg: "h-12 pl-4 pr-10 text-[15px]",
};

export const Select = React.forwardRef(function Select(
  {
    className,
    options = [],
    placeholder,
    size = "md",
    invalid = false,
    disabled = false,
    "data-testid": testId,
    children,
    ...rest
  },
  ref
) {
  return (
    <select
      ref={ref}
      disabled={disabled}
      aria-invalid={invalid || undefined}
      data-testid={testId}
      className={cn(
        "block w-full appearance-none rounded-[var(--ds-radius-md)]",
        "bg-[var(--ds-color-surface)] text-[var(--ds-color-ink)]",
        "border",
        invalid
          ? "border-[var(--ds-color-danger)]"
          : "border-[var(--ds-color-border-strong)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ds-color-background)]",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        SIZES[size] || SIZES.md,
        className
      )}
      style={{
        transitionDuration: "var(--ds-duration-fast)",
        // Chevron drawn via inline SVG data-URI so we don't ship an
        // icon dependency here. Kept intentionally boring.
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='none' stroke='%2354524A' stroke-width='1.6' d='M1 1l5 5 5-5'/></svg>\")",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 12px center",
      }}
      {...rest}
    >
      {placeholder !== undefined && (
        <option value="" disabled hidden>
          {placeholder}
        </option>
      )}
      {children ||
        options.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
    </select>
  );
});

Select.displayName = "DSSelect";

export default Select;
